import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { ComponentProps } from 'lib/component-props';
import useHelpers from 'hooks/helpers';
import DisplayingRange from 'components/includes/DisplayingRange';
import { useI18n } from 'next-localization';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import ArticleCard from 'components/Tiles/ArticleCard';
import Pagination from 'components/includes/Pagination';
import { useEffect, useMemo, useState } from 'react';
import { useLazyArticlesSearchQuery } from '@/store/articles/services/articleSearch';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { getArticlesData } from '@/store/articles';
import styles from '@/components/Tiles/ArticleCard.module.scss';

const ITEMS_PER_PAGE_FALLBACK = 20;

const ArticleCards = ({ rendering }: ComponentProps): JSX.Element => {
  const { anchorId } = useHelpers(rendering);
  const { t } = useI18n();
  const router = useRouter();
  const { page } = router.query;
  const { updateUrlParams } = useHelpers();
  const { take } = updateUrlParams();
  const [currentPage, setCurrentPage] = useState(Number(page) || 1);
  const [itemsPerPage, setItemsPerPage] = useState();
  const { sitecoreContext } = useSitecoreContext();
  const [fetchArticles, { data, isFetching }] = useLazyArticlesSearchQuery();
  const { appliedFilters, contentType } = useSelector(getArticlesData);

  const articlesPerPageOptions = useMemo(() => {
    const output = [];
    const step = 20;
    for (let i = 0; i < 5; i++) {
      const stepNumber = (i + 1) * step;
      output.push({
        value: stepNumber,
        label: stepNumber,
        selected: +itemsPerPage === stepNumber,
      });
    }
    return output;
  }, [itemsPerPage]);

  useEffect(() => {
    setItemsPerPage(take || ITEMS_PER_PAGE_FALLBACK);
  }, [take]);

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    }
  }, [page]);

  useEffect(() => {
    setCurrentPage(1);
    updateUrlParams([
      {
        name: 'page',
        value: '',
      },
    ]);
  }, [appliedFilters]);

  useEffect(() => {
    if (itemsPerPage) {
      fetchArticles({
        filters: appliedFilters,
        search: {
          sites: [sitecoreContext.site?.name],
          language: sitecoreContext.language,
          entityTypes: ['article'],
          contentTypes: (contentType && [contentType]) || null,
        },
        take: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters,
    itemsPerPage,
    currentPage,
    sitecoreContext.language,
    sitecoreContext.site?.name,
  ]);

  const onPageSelect = (selectedPage: number) => {
    setCurrentPage(selectedPage);
    updateUrlParams([
      {
        name: 'page',
        value: selectedPage === 1 ? '' : selectedPage,
      },
    ]);
  };

  const handleSelect = ({ value }: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
    updateUrlParams([
      {
        name: 'take',
        value: value === 20 ? '' : value,
      },
      {
        name: 'page',
        value: '',
      },
    ]);
  };

  const mappedArticleData = useMemo(() => {
    return (
      data?.content?.map((item) => ({
        articleImage: item.article_image,
        articlePublicationDate: {
          formattedDate: item.article_publication_date,
        },
        articleStrapline: {
          value: item.article_strapline,
        },
        articleTitle: {
          value: item.article_title,
        },
        articleType: item.article_type,
        url: item.article_url,
        id: item.id,
        category: (item.article_categories && item.article_categories[0]) || '',
        name: item.article_title,
        publicationDate: item.article_publication_date,
        duration: {
          value: item.article_duration,
        },
      })) || []
    );
  }, [data]);

  return (
    <section id={anchorId} data-e2e-component-uid={rendering.uid} className="container pb-5">
      <div className="d-flex flex-column-reverse flex-sm-row align-items-sm-center mb-5 w-100 justify-content-between">
        <DisplayingRange
          className="footnote text-grey"
          page={currentPage}
          dictionaryKey="paginationDisplayingInsightsResults"
          take={itemsPerPage}
          total={data?.total_item}
        />
        <div className="d-flex align-items-center ms-auto ms-sm-0 mb-2 mb-sm-0">
          <p className="form-label me-2">{t('paginationShow')}</p>
          <AbrdnSelect
            key={itemsPerPage}
            options={articlesPerPageOptions}
            onChange={handleSelect}
            defaultValue={articlesPerPageOptions.find((option) => option.selected)}
          />
        </div>
      </div>
      <div className={`row d-flex flex-wrap ${mappedArticleData?.length < 4 ? 'w-100' : ''}`}>
        {mappedArticleData?.map((article) => (
          <div className="col-12 col-md-6 col-lg-3 mb-3" key={article.id}>
            <ArticleCard fields={article} />
          </div>
        ))}
      </div>
      <div
        className={`d-flex align-items-lg-center mt-2 mt-md-3 mt-xl-5 mb-0 mb-md-1 mb-xl-3 w-100 ${styles.relative}`}
      >
        <DisplayingRange
          className="footnote text-grey"
          dictionaryKey="paginationDisplayingInsightsResults"
          page={currentPage}
          take={itemsPerPage}
          total={data?.total_item}
        />
        {!!data?.content && (
          <Pagination
            currentPage={currentPage}
            maxPages={Math.ceil(data.total_item / itemsPerPage)}
            className={`${styles.pagination} justify-content-center`}
            onSelect={onPageSelect}
            isLoading={isFetching}
          />
        )}
      </div>
    </section>
  );
};

export default ArticleCards;
